// Legal page content — Privacy Policy, Terms of Use, Cookie Policy.
// Rendered by src/pages/LegalPage.jsx. Written for ElectroMax Systems /
// ElectroMax Electrical Ltd. (BC, Canada) in plain language.

export const LEGAL_PAGES = {
  "privacy-policy": {
    slug: "privacy-policy",
    crumb: "Privacy Policy",
    kicker: "— Legal",
    h1A: "Privacy",
    h1B: "Policy.",
    metaTitle: "Privacy Policy | ElectroMax Systems",
    metaDesc: "How ElectroMax Systems collects, uses, and protects your personal information — quote forms, contact details, and your rights under BC privacy law.",
    updated: "September 2026",
    intro: "ElectroMax Systems (\u201cwe\u201d, \u201cus\u201d) is operated by ElectroMax Electrical Ltd., 11045 78 Ave, Delta, BC V4C 1N6. This policy explains what personal information we collect through this website, why we collect it, and what we never do with it. We follow British Columbia\u2019s Personal Information Protection Act (PIPA) and Canada\u2019s federal privacy principles.",
    sections: [
      {
        h: "What We Collect",
        body: ["We only collect what a quote or a conversation actually requires:"],
        items: [
          "Contact details you give us — first and last name, phone number or email address",
          "Job details — your city, the services you select, and whatever you write in the project message",
          "Correspondence — records of calls, texts, or emails so we can follow up on your request",
        ],
      },
      {
        h: "How We Use It",
        body: ["Your information is used for exactly two things:"],
        items: [
          "Responding to your quote request or question — usually within one business hour",
          "Scheduling and completing electrical work you hire us for, including permits and inspections where required",
        ],
      },
      {
        h: "What We Never Do",
        body: [
          "We do not sell, rent, or trade your personal information to anyone. We do not share your details with lead-generation platforms, marketing lists, or other contractors. The electrician who quotes your job is the one who gets your number — nobody else.",
        ],
      },
      {
        h: "Who Handles Your Data",
        body: [
          "Quote form submissions are stored securely and a notification is delivered to our office through a third-party transactional email provider, used only to send us your request. Our website hosting and infrastructure providers process data only as needed to run this site. None of them are permitted to use your information for their own purposes.",
        ],
      },
      {
        h: "How Long We Keep It",
        body: [
          "Quote requests and correspondence are kept only as long as needed to serve you and meet our legal and insurance obligations as a licensed BC electrical contractor — for example, permit and inspection records tied to completed work. When information is no longer needed, it is deleted or anonymized.",
        ],
      },
      {
        h: "How We Protect It",
        body: [
          "Access to customer information is limited to the people who need it to do your job. Our systems use encrypted connections (HTTPS) and access controls. No method of transmission over the internet is perfectly secure, but we treat your information the way we treat your panel — carefully, and to code.",
        ],
      },
      {
        h: "Your Rights",
        body: ["Under BC privacy law, you can:"],
        items: [
          "Ask what personal information we hold about you",
          "Ask us to correct it",
          "Ask us to delete it, subject to legal and insurance record-keeping requirements",
        ],
      },
      {
        h: "Contact Us About Privacy",
        body: [
          "Questions, corrections, or deletion requests: email info@electromax.ca or call 604-754-1050. You can also write to ElectroMax Electrical Ltd., 11045 78 Ave, Delta, BC V4C 1N6.",
        ],
      },
    ],
  },
  "terms-of-use": {
    slug: "terms-of-use",
    crumb: "Terms of Use",
    kicker: "— Legal",
    h1A: "Terms of",
    h1B: "Use.",
    metaTitle: "Terms of Use | ElectroMax Systems",
    metaDesc: "The terms for using the ElectroMax Systems website — estimates, content accuracy, intellectual property, and governing law in British Columbia.",
    updated: "September 2026",
    intro: "These terms govern your use of the ElectroMax Systems website, operated by ElectroMax Electrical Ltd. (\u201cwe\u201d, \u201cus\u201d), 11045 78 Ave, Delta, BC V4C 1N6. By using this site, you accept these terms. If you don\u2019t agree, please don\u2019t use the site — call us instead at 604-754-1050, we\u2019re happy to talk.",
    sections: [
      {
        h: "What This Website Is",
        body: [
          "This website provides general information about our electrical services across Metro Vancouver and the Fraser Valley, and a way to request a free estimate. Content on this site — including pricing ranges, rebate details, and guides — is general information, not a quote, and not electrical advice for your specific situation.",
        ],
      },
      {
        h: "Estimates & Quotes",
        body: [
          "Submitting the quote form or contacting us does not create a contract for work. A written quote is provided after we assess your job, and work begins only once that quote is accepted. Pricing ranges shown on this site are typical bands for planning purposes — your written quote is the number that counts.",
        ],
      },
      {
        h: "Rebate & Program Information",
        body: [
          "Rebate amounts and program rules (CleanBC, BC Hydro, and others) are set by those programs and change without notice. We work hard to keep our rebate pages current, but the program\u2019s own published terms at the time of your application are the final word. Eligibility is determined by the program, not by us.",
        ],
      },
      {
        h: "Permits & Code",
        body: [
          "Where work requires a permit, we file it and manage the inspection as part of the job. Electrical work must be performed by licensed electricians — nothing on this site should be read as encouragement to attempt electrical work yourself.",
        ],
      },
      {
        h: "Intellectual Property",
        body: [
          "All content on this site — text, design, logos, and images — belongs to ElectroMax Electrical Ltd. or its licensors. You may not copy, republish, or reuse it without written permission. Review text belongs to the customers who wrote it and is reproduced with permission.",
        ],
      },
      {
        h: "Third-Party Links",
        body: [
          "This site links to external pages — rebate programs, licensing bodies, and industry associations. We don\u2019t control those sites and aren\u2019t responsible for their content or availability.",
        ],
      },
      {
        h: "Limitation of Liability",
        body: [
          "The website is provided \u201cas is\u201d. We work to keep it accurate and available, but we don\u2019t warrant that it will be error-free or uninterrupted. To the fullest extent permitted by law, we are not liable for damages arising from use of this website. This limitation does not affect our obligations for work we actually perform, which are governed by your written quote and BC law.",
        ],
      },
      {
        h: "Governing Law",
        body: [
          "These terms are governed by the laws of British Columbia and Canada. Any dispute relating to this website falls under the courts of British Columbia.",
        ],
      },
      {
        h: "Changes to These Terms",
        body: [
          "We may update these terms from time to time. The date at the top of this page shows the latest revision. Questions: info@electromax.ca or 604-754-1050.",
        ],
      },
    ],
  },
  "cookie-policy": {
    slug: "cookie-policy",
    crumb: "Cookie Policy",
    kicker: "— Legal",
    h1A: "Cookie",
    h1B: "Policy.",
    metaTitle: "Cookie Policy | ElectroMax Systems",
    metaDesc: "How the ElectroMax Systems website uses cookies and similar technologies — the short version: no advertising trackers, no cookie banners needed.",
    updated: "September 2026",
    intro: "This policy explains how the ElectroMax Systems website uses cookies and similar technologies. The short version: this site runs without advertising trackers, cross-site profiling, or third-party marketing cookies.",
    sections: [
      {
        h: "What Cookies Are",
        body: [
          "Cookies are small text files a website stores on your device. Similar technologies — like browser local storage — do the same job. They\u2019re used to remember preferences, keep sites secure, and (on many sites) track you for advertising. This section exists so you know exactly where we stand.",
        ],
      },
      {
        h: "What This Site Uses",
        body: ["This website uses only what it needs to function:"],
        items: [
          "Essential technical storage — required for the site to load and navigate correctly",
          "No advertising cookies",
          "No cross-site tracking or profiling",
          "No social media tracking pixels",
        ],
      },
      {
        h: "Third-Party Services",
        body: [
          "This site loads fonts from external font providers (Fontshare and Google Fonts) to display our typography. These providers may log standard technical request data (like your IP address) when your browser downloads the font files — that\u2019s how the web works, and it\u2019s covered by their own privacy policies. Links to external sites (rebate programs, licensing bodies, associations) are governed by those sites\u2019 policies once you leave ours.",
        ],
      },
      {
        h: "Analytics",
        body: [
          "If we add privacy-friendly analytics in the future to understand which pages help visitors, we will update this page before it goes live. Analytics, if used, will be aggregated — never tied to your name, email, or quote request.",
        ],
      },
      {
        h: "Your Choices",
        body: [
          "You can block or delete cookies at any time through your browser settings. Because this site doesn\u2019t rely on non-essential cookies, blocking them won\u2019t break anything — the site will look and work the same.",
        ],
      },
      {
        h: "Changes & Questions",
        body: [
          "We\u2019ll update this page if our use of cookies changes — the date at the top shows the latest revision. Questions: info@electromax.ca or 604-754-1050.",
        ],
      },
    ],
  },
};
